import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { useTheme } from '../../../../context/ThemeContext';
import { Sun, Moon, Shield, Building2, Briefcase, User, Warehouse, CheckCircle2, ChevronDown, UserCheck } from 'lucide-react';
import { getRoleDescription } from '../../../../config/roles';

const ROLE_METADATA = {
    SYSTEM_ADMIN: {
        title: 'System Administrator',
        description: 'Full system governance, tenants & plans',
        icon: Shield,
        badgeClass: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800'
    },
    TENANT_OWNER: {
        title: 'Supplier / Owner',
        description: 'Complete business access & management',
        icon: Building2,
        badgeClass: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
    },
    TENANT_ADMIN: {
        title: 'Supplier Admin',
        description: 'Administrative & operational management',
        icon: Building2,
        badgeClass: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
    },
    SALES_MANAGER: {
        title: 'Sales Manager',
        description: 'Team sales tracking, orders & inventory',
        icon: Briefcase,
        badgeClass: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
    },
    SALES_PERSON: {
        title: 'Sales Person',
        description: 'Sales orders, POS sales & customer catalog',
        icon: User,
        badgeClass: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
    },
    WAREHOUSE_PERSON: {
        title: 'Warehouse Staff',
        description: 'Stock transfers, loading & offloading requests',
        icon: Warehouse,
        badgeClass: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800'
    }
};

const getRoleMeta = (roleCode) => {
    return ROLE_METADATA[roleCode] || {
        title: getRoleDescription(roleCode),
        description: 'Role permissions and access view',
        icon: UserCheck,
        badgeClass: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
    };
};

const TopHeader = ({ supplierLogoUrl, companyName, currentRole, availableRoles = [], onRoleChange }) => {
    const { user } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const currentMeta = getRoleMeta(currentRole);
    const CurrentRoleIcon = currentMeta.icon;

    // Close dropdown when clicking or tapping outside anywhere
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isDropdownOpen]);

    const userName = user?.name || user?.firstName || user?.username || 'User';

    return (
        <header className="sticky top-0 z-40 w-full bg-[var(--color-bg-primary)]/95 backdrop-blur-md border-b border-[var(--color-border)] shadow-xs transition-all">
            {/* Top Bar: Identity & User Info */}
            <div className="px-3 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-2">
                {/* LEFT: Product Branding (Yaseero on mobile/tablet) + Tenant Branding */}
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    {/* Yaseero Product Brand Lockup (Hidden on desktop md+ as desktop sidebar has Yaseero logo) */}
                    <div className="flex md:hidden items-center gap-1.5 sm:gap-2 shrink-0" title="Yaseero SaaS Platform">
                        <img 
                            src="/logo.jpg" 
                            alt="Yaseero" 
                            className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg object-contain shadow-xs border border-slate-200 dark:border-slate-800 bg-white"
                        />
                        <span className="hidden xs:inline-block font-black text-sm sm:text-base tracking-tight text-slate-900 dark:text-white">
                            Yaseero
                        </span>
                    </div>

                    {/* Divider (Hidden on desktop md+) */}
                    <div className="md:hidden h-5 sm:h-6 w-px bg-slate-200 dark:bg-slate-700/80 shrink-0" />

                    {/* Tenant Branding (Store/Supplier) */}
                    <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                        {supplierLogoUrl ? (
                            <img 
                                src={supplierLogoUrl} 
                                alt={companyName || 'Company'} 
                                className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border border-slate-200 dark:border-slate-700 object-cover shadow-xs bg-white shrink-0"
                            />
                        ) : (
                            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 flex items-center justify-center text-white text-xs sm:text-sm font-black shadow-xs border border-white/20 shrink-0">
                                {companyName?.charAt(0)?.toUpperCase() || 'S'}
                            </div>
                        )}
                        
                        <div className="flex flex-col justify-center min-w-0">
                            <span className="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-200 tracking-tight truncate max-w-[100px] xs:max-w-[140px] sm:max-w-[200px]">
                                {companyName || 'Supplier Portal'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* DESKTOP CENTER ROLE SWITCHER (Visible sm and up) */}
                <div className="hidden sm:flex flex-1 justify-center items-center px-2 min-w-0">
                    {availableRoles.length > 1 ? (
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setIsDropdownOpen(prev => !prev)}
                                className="flex items-center gap-2 px-4 py-1.5 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-700 hover:to-indigo-800 text-white font-extrabold text-xs sm:text-sm shadow-md shadow-blue-500/25 active:scale-95 transition-all cursor-pointer border border-blue-400/30"
                                title="Click to switch active role"
                            >
                                <CurrentRoleIcon className="w-4 h-4 shrink-0 text-white" />
                                <span>{currentMeta.title}</span>
                                <ChevronDown className={`w-4 h-4 shrink-0 opacity-90 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Dropdown Menu */}
                            {isDropdownOpen && (
                                <>
                                    <div className="fixed inset-0 z-40 bg-black/10 backdrop-blur-[1px]" onClick={() => setIsDropdownOpen(false)} />
                                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 w-80 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl p-2.5 space-y-1.5 animate-in fade-in slide-in-from-top-2 duration-150">
                                        <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <UserCheck className="w-4 h-4 text-blue-600" />
                                                <span className="text-xs font-black text-slate-900 dark:text-white uppercase tracking-wider">Switch Active Role</span>
                                            </div>
                                            <span className="text-[10px] font-extrabold text-blue-600 bg-blue-50 dark:bg-blue-900/40 px-2 py-0.5 rounded-full border border-blue-100 dark:border-blue-800">
                                                {availableRoles.length} Available
                                            </span>
                                        </div>

                                        <div className="space-y-1 max-h-[60vh] overflow-y-auto custom-scrollbar">
                                            {availableRoles.map((roleCode) => {
                                                const meta = getRoleMeta(roleCode);
                                                const RoleIcon = meta.icon;
                                                const isSelected = roleCode === currentRole;

                                                return (
                                                    <button
                                                        key={roleCode}
                                                        onClick={() => {
                                                            if (onRoleChange && !isSelected) onRoleChange(roleCode);
                                                            setIsDropdownOpen(false);
                                                        }}
                                                        className={`w-full p-2.5 rounded-xl border text-left flex items-center justify-between gap-3 transition-all cursor-pointer ${
                                                            isSelected
                                                                ? 'bg-blue-50 dark:bg-blue-950/60 border-blue-500/80 dark:border-blue-500 text-blue-900 dark:text-white font-bold shadow-xs'
                                                                : 'bg-transparent border-transparent hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2.5 min-w-0">
                                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border ${meta.badgeClass}`}>
                                                                <RoleIcon className="w-4 h-4" />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="text-xs font-extrabold truncate leading-tight">{meta.title}</p>
                                                                <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400 truncate mt-0.5">{meta.description}</p>
                                                            </div>
                                                        </div>

                                                        {isSelected && (
                                                            <div className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                                                                <CheckCircle2 className="w-3.5 h-3.5" />
                                                            </div>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : availableRoles.length === 1 ? (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-bold text-xs">
                            <CurrentRoleIcon className="w-3.5 h-3.5 text-slate-500" />
                            <span>{currentMeta.title}</span>
                        </div>
                    ) : null}
                </div>

                {/* RIGHT: Utilities & User Profile (Full Name Visible) */}
                <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                    <button 
                        onClick={toggleTheme}
                        className="p-1.5 sm:p-2 rounded-full text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] transition-colors shrink-0"
                        title="Toggle Theme"
                    >
                        {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
                    </button>

                    <div className="flex items-center gap-2 pl-2 border-l border-[var(--color-border)] shrink-0">
                        <div className="flex flex-col items-end">
                            <span className="text-xs sm:text-xs font-bold text-[var(--color-text-primary)] leading-tight whitespace-nowrap">
                                {userName}
                            </span>
                            <span className="hidden sm:block text-[10px] text-[var(--color-text-secondary)] leading-tight mt-0.5">
                                {currentMeta.title}
                            </span>
                        </div>
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 border border-[var(--color-border)] flex items-center justify-center text-white text-xs font-black shrink-0">
                            {userName.charAt(0).toUpperCase()}
                        </div>
                    </div>
                </div>
            </div>

            {/* MOBILE ROLE SWITCHER SUB-BAR (Visible < sm, 100% Zero Truncation!) */}
            {availableRoles.length > 1 && (
                <div className="sm:hidden px-3 pb-2 pt-0.5 flex justify-center border-t border-[var(--color-border-soft)]/50 bg-[var(--color-bg-primary)]" ref={dropdownRef}>
                    <button
                        onClick={() => setIsDropdownOpen(prev => !prev)}
                        className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-700 hover:to-indigo-800 text-white font-extrabold text-xs shadow-xs active:scale-98 transition-all cursor-pointer border border-blue-400/30"
                        title="Click to switch active role"
                    >
                        <CurrentRoleIcon className="w-3.5 h-3.5 shrink-0 text-white" />
                        <span className="truncate">{currentMeta.title}</span>
                        <ChevronDown className={`w-3.5 h-3.5 shrink-0 opacity-90 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Mobile Dropdown Menu */}
                    {isDropdownOpen && (
                        <>
                            <div className="fixed inset-0 z-40 bg-black/10 backdrop-blur-[1px]" onClick={() => setIsDropdownOpen(false)} />
                            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 z-50 w-[calc(100vw-20px)] max-w-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl p-2.5 space-y-1.5 animate-in fade-in slide-in-from-top-2 duration-150">
                                <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <UserCheck className="w-4 h-4 text-blue-600" />
                                        <span className="text-xs font-black text-slate-900 dark:text-white uppercase tracking-wider">Switch Active Role</span>
                                    </div>
                                    <span className="text-[10px] font-extrabold text-blue-600 bg-blue-50 dark:bg-blue-900/40 px-2 py-0.5 rounded-full border border-blue-100 dark:border-blue-800">
                                        {availableRoles.length} Available
                                    </span>
                                </div>

                                <div className="space-y-1 max-h-[60vh] overflow-y-auto custom-scrollbar">
                                    {availableRoles.map((roleCode) => {
                                        const meta = getRoleMeta(roleCode);
                                        const RoleIcon = meta.icon;
                                        const isSelected = roleCode === currentRole;

                                        return (
                                            <button
                                                key={roleCode}
                                                onClick={() => {
                                                    if (onRoleChange && !isSelected) onRoleChange(roleCode);
                                                    setIsDropdownOpen(false);
                                                }}
                                                className={`w-full p-2.5 rounded-xl border text-left flex items-center justify-between gap-3 transition-all cursor-pointer ${
                                                    isSelected
                                                        ? 'bg-blue-50 dark:bg-blue-950/60 border-blue-500/80 dark:border-blue-500 text-blue-900 dark:text-white font-bold shadow-xs'
                                                        : 'bg-transparent border-transparent hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                                                }`}
                                            >
                                                <div className="flex items-center gap-2.5 min-w-0">
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border ${meta.badgeClass}`}>
                                                        <RoleIcon className="w-4 h-4" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-xs font-extrabold truncate leading-tight">{meta.title}</p>
                                                        <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400 truncate mt-0.5">{meta.description}</p>
                                                    </div>
                                                </div>

                                                {isSelected && (
                                                    <div className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </header>
    );
};

export default TopHeader;


